import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Calculator, Download, Send, QrCode, ClipboardList, CheckSquare, Square, AlertTriangle, Wallet } from '../lib/icons';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  fetchReceptions,
  fetchReceptionsAnnulees,
  decompte,
  familleDe,
  labelQualite,
  labelMode,
  nomTiers,
  ecartRendement,
  huileTheorique,
  adminModifierReception,
  adminSupprimerReception,
  planifierLots,
  numeroOf,
  reglerEmballages,
  fetchFinances,
  etatPaiement,
  bonIncomplet,
  lignesDuBon,
  QUALITES,
  STATUTS,
} from '../lib/dataProvider';
import { CamionsSurSite } from '../components/reception/CamionsSurSite';
import { SolderButton, PaiementBadge, SolderCamionButton } from '../components/reception/SolderButton';
import { AvanceFournisseurDialog } from '../components/reception/AvanceFournisseurDialog';
import { EncaissementClientDialog } from '../components/reception/EncaissementClientDialog';
import { fetchAvancesEnCours, nonTriture, fetchLotsANegocier } from '../lib/dataProvider';
import { ActionsAdmin } from '../components/shared/ActionsAdmin';
import { BadgeFacture } from '../components/facturation/BadgeFacture';
import { fmt, StatutBadge, QualiteBadge, ParcoursBadge, PageTitle, StatBlock } from '../components/shared/badges';
import { couleurGain } from '../lib/couleurs';
import { ReceptionForm } from '../components/reception/ReceptionForm';
import { FixationPrixDialog } from '../components/reception/FixationPrixDialog';
import { useAuth } from '../lib/auth';
import { DecompteDialog } from '../components/reception/DecompteDialog';
import { EtiquettesQR } from '../components/print/EtiquettesQR';
import { PartageLienDialog } from '../components/reception/PartageLienDialog';
import { PeseeEclairForm } from '../components/reception/PeseeEclairForm';
import { CompleterBonDialog } from '../components/reception/CompleterBonDialog';
import { PeriodeFiltre, dansPeriode } from '../components/shared/PeriodeFiltre';

const FILTRES = [
  { id: 'tous', label: 'Tous' },
  { id: 'nontritures', label: 'Non triturés' },
  { id: 'impayes', label: 'Non soldés' },
  { id: 'client', label: 'Clients (prestation)' },
  { id: 'noble', label: 'Achats nobles' },
  { id: 'lampante', label: 'Achats lampantes' },
];

const match = (r, f) => {
  if (f === 'tous') return true;
  if (f === 'nontritures') return nonTriture(r);
  if (f === 'impayes') return etatPaiement(r).code === 'impaye';
  if (f === 'client') return r.parcours === 'client';
  return r.parcours === 'fournisseur' && familleDe(r.qualite) === f;
};

export default function ReceptionOlives() {
  const [rows, setRows] = useState([]);
  const [filtre, setFiltre] = useState('tous');
  const [params, setParams] = useSearchParams();
  const [formOpen, setFormOpen] = useState(false);
  const [decompteRow, setDecompteRow] = useState(null);
  const { user } = useAuth();
  const estAdmin = user?.role === 'Administrateur';
  const [annulees, setAnnulees] = useState([]);
  const [etiquettes, setEtiquettes] = useState(null);
  const [partage, setPartage] = useState(null);
  const [planif, setPlanif] = useState([]);
  const [comptes, setComptes] = useState([]);
  const [encaisse, setEncaisse] = useState(null);
  const [avance, setAvance] = useState(false);
  const [encaissement, setEncaissement] = useState(false);
  const [fixation, setFixation] = useState(false);
  const [lotsANegocier, setLotsANegocier] = useState(0);
  const [avances, setAvances] = useState([]);
  const [eclair, setEclair] = useState(params.get('eclair') === '1' || params.get('nouveau') === '1');
  const [completer, setCompleter] = useState(null);

  // Ouverture directe depuis les Smart Actions du Copilote (Vigie).
  useEffect(() => {
    const enc = params.get('encaisser');
    if (!enc || !rows.length) return;
    const r = rows.find((x) => x.id === enc);
    if (r && (r.emballagesMontant || 0) > 0 && r.emballagesRegle !== true) setEncaisse(r);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, rows]);

  const load = useCallback(() => {
    fetchReceptions().then(setRows);
    fetchReceptionsAnnulees().then(setAnnulees);
    fetchAvancesEnCours().then(setAvances);
    fetchLotsANegocier().then((l) => setLotsANegocier(l.length));
    fetchFinances().then((f) => setComptes(f.comptes.filter((c) => c.type !== 'Banque')));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const enAttente = useMemo(() => rows.filter((r) => r.statut === 'En attente'), [rows]);
  const togglePlanif = (id) =>
    setPlanif((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const planifier = async () => {
    const { error, of } = await planifierLots(planif, user?.name);
    if (error) return toast.error(error);
    toast.success(`Ordre de fabrication ${of.numero} créé — ${planif.length} lot(s) planifié(s)`);
    setPlanif([]);
    load();
  };

  const validerEncaissement = async (compteId) => {
    const { error, montant } = await reglerEmballages(encaisse.id, { compteId, mode: 'Espèces' });
    if (error) return toast.error(error);
    toast.success(`Emballages encaissés : ${fmt(montant, 3)} TND`);
    setEncaisse(null);
    load();
  };

  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const visibles = useMemo(
    () => rows.filter((r) => r.statut !== 'Sur site' && match(r, filtre) && dansPeriode(r.date, du, au)),
    [rows, filtre, du, au]
  );
  const surSite = useMemo(() => rows.filter((r) => r.statut === 'Sur site'), [rows]);
  const sansQualite = useMemo(() => rows.filter((r) => !r.qualite && nonTriture(r)), [rows]);

  const bilan = useMemo(() => {
    const agg = (list) => ({
      n: list.length,
      poids: list.reduce((s, r) => s + decompte(r).poidsNet, 0),
      huile: list.reduce((s, r) => s + decompte(r).huile, 0),
      montant: list.reduce((s, r) => s + decompte(r).montant, 0),
      emballages: list.reduce((s, r) => s + decompte(r).emballages, 0),
    });
    return {
      client: agg(rows.filter((r) => r.parcours === 'client')),
      noble: agg(rows.filter((r) => r.parcours === 'fournisseur' && familleDe(r.qualite) === 'noble')),
      lampante: agg(rows.filter((r) => r.parcours === 'fournisseur' && familleDe(r.qualite) === 'lampante')),
    };
  }, [rows]);

  const exportCsv = () => {
    const head = ['Réf', 'Date', 'Parcours', 'Tiers', 'Qualité', 'Mode', 'Poids net', 'Rdt', 'Montant', 'Statut'];
    const body = visibles.map((r) => {
      const c = decompte(r);
      return [r.id, r.date, r.parcours, nomTiers(r.tiersId), r.qualite ? labelQualite(r.qualite) : '—', labelMode(r.mode), c.poidsNet, c.rendement, c.montant.toFixed(2), r.statut].join(';');
    });
    const url = URL.createObjectURL(new Blob([[head.join(';'), ...body].join('\n')], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = du || au ? `receptions_${du || 'debut'}_${au || 'fin'}.csv` : 'receptions.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Export CSV généré');
  };

  return (
    <div data-testid="reception-page" className="space-y-6">
      <PageTitle
        emoji="⚖️"
        titre="Réception"
        sous="Deux parcours étanches : prestation client ou achat fournisseur"
        action={
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="open-avance-fournisseur"
              onClick={() => setAvance(true)}
              className="flex h-10 items-center gap-2 border-2 border-amber-600 px-4 font-heading text-sm font-bold text-amber-700 transition-colors duration-150 hover:bg-amber-600 hover:text-white"
            >
              Avance fournisseur
            </button>
            <button
              type="button"
              data-testid="open-encaisser-client"
              onClick={() => setEncaissement(true)}
              className="flex h-10 items-center gap-2 border-2 border-emerald-600 px-4 font-heading text-sm font-bold text-emerald-700 transition-colors duration-150 hover:bg-emerald-600 hover:text-white"
            >
              Encaisser client
            </button>
            <button
              type="button"
              data-testid="open-fixation-prix"
              onClick={() => setFixation(true)}
              className="flex h-10 items-center gap-2 border-2 border-slate-900 px-4 font-heading text-sm font-bold text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
            >
              Achats à négocier{lotsANegocier > 0 ? ` (${lotsANegocier})` : ''}
            </button>
            <button
              type="button"
              data-testid="open-reception-form"
              onClick={() => setFormOpen(true)}
              className="flex h-10 items-center gap-2 border border-slate-300 px-4 font-heading text-sm font-bold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
            >
              Saisie détaillée
            </button>
          <button
            type="button"
            data-testid="open-pesee-eclair"
            onClick={() => setEclair(true)}
            className="flex h-10 items-center gap-2 bg-slate-900 px-4 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700"
          >
            <Plus size={16} /> Pesée éclair
          </button>
          </div>
        }
      />

      {avances.length > 0 && (
        <section data-testid="avances-en-cours" className="border border-amber-200 border-l-4 border-l-amber-500 bg-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
            <p className="font-heading text-sm font-bold text-slate-900">
              Avances fournisseurs disponibles
              <span data-testid="avances-total" className="ml-2 font-data text-amber-700">{fmt(avances.reduce((s, a) => s + a.disponible, 0), 3)} TND</span>
            </p>
            {avances.map((a) => (
              <span key={a.tiersId} data-testid={`avance-dispo-${a.tiersId}`} className="font-data text-xs text-slate-700">
                {a.nom} : <b>{fmt(a.disponible, 3)}</b> TND
              </span>
            ))}
          </div>
          <p className="mt-1 text-[11px] text-slate-500">Imputées automatiquement sur les prochains bons de ces fournisseurs dès que le montant est connu.</p>
        </section>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <StatBlock
          testid="bilan-client"
          titre="Prestation clients"
          accent="border-l-sky-600"
          items={[
            ['Dossiers', fmt(bilan.client.n)],
            ['Poids net (Kg)', fmt(bilan.client.poids)],
            ['Emballages (TND)', fmt(bilan.client.emballages, 2)],
            ['Facturé (TND)', fmt(bilan.client.montant, 2)],
          ]}
        />
        <StatBlock
          testid="bilan-noble"
          titre="Achats nobles — Vierge / Extra"
          accent="border-l-emerald-600"
          items={[
            ['Achats', fmt(bilan.noble.n)],
            ['Poids net (Kg)', fmt(bilan.noble.poids)],
            ['Huile (L)', fmt(bilan.noble.huile)],
            ['Montant (TND)', fmt(bilan.noble.montant, 2)],
          ]}
        />
        <StatBlock
          testid="bilan-lampante"
          titre="Achats lampantes — comptabilité cloisonnée"
          accent="border-l-orange-500"
          items={[
            ['Achats', fmt(bilan.lampante.n)],
            ['Poids net (Kg)', fmt(bilan.lampante.poids)],
            ['Huile (L)', fmt(bilan.lampante.huile)],
            ['Montant (TND)', fmt(bilan.lampante.montant, 2)],
          ]}
        />
      </div>

      <CamionsSurSite
        camions={surSite}
        onCloture={(row) => {
          load();
          // Post-tare : finalisation immédiate des détails de l'opération.
          setCompleter(row);
        }}
      />

      {sansQualite.length > 0 && (
        <section data-testid="alerte-sans-qualite" className="border-2 border-red-600 bg-red-50 px-4 py-3">
          <p className="font-heading text-sm font-bold text-red-800">
            {sansQualite.length} bon(s) sans qualité — invisibles des files du planning atelier
          </p>
          <p className="mt-0.5 text-xs text-red-700">
            La qualité (Extra-Vierge / Vierge / Lampante) est obligatoire pour router le bon vers sa
            file d'atelier. Complétez-les :
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {sansQualite.map((r) => (
              <button
                key={r.id}
                type="button"
                data-testid={`completer-sans-qualite-${r.id}`}
                onClick={() => setCompleter(r)}
                className="flex h-9 items-center gap-1.5 border-2 border-red-600 bg-white px-2.5 font-data text-xs font-bold text-red-700 transition-colors duration-150 hover:bg-red-600 hover:text-white"
              >
                {r.id} · {nomTiers(r.tiersId)}
              </button>
            ))}
          </div>
        </section>
      )}

      <section data-testid="reception-table-section" className="border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div className="flex gap-px bg-slate-200">
            {FILTRES.map((f) => (
              <button
                key={f.id}
                type="button"
                data-testid={`filtre-${f.id}`}
                onClick={() => setFiltre(f.id)}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                  filtre === f.id ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <PeriodeFiltre du={du} au={au} setDu={setDu} setAu={setAu} testid="periode-reception" total={rows.length} filtres={visibles.length} />
            <button
              type="button"
              data-testid="reception-export-csv"
              onClick={exportCsv}
              className="flex items-center gap-2 border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
            >
              <Download size={15} /> Exporter CSV
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          {planif.length > 0 && (
            <div
              data-testid="barre-planification"
              className="mb-3 flex flex-wrap items-center justify-between gap-3 border-2 border-slate-900 bg-slate-50 px-4 py-3"
            >
              <p className="font-data text-sm text-slate-800">
                {planif.length} lot(s) sélectionné(s) —{' '}
                {fmt(rows.filter((r) => planif.includes(r.id)).reduce((s, r) => s + decompte(r).poidsNet, 0))} Kg
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="planifier-atelier"
                  onClick={planifier}
                  className="flex h-10 items-center gap-2 bg-slate-900 px-4 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700"
                >
                  <ClipboardList size={15} /> Planifier pour l'atelier
                </button>
                <button
                  type="button"
                  data-testid="planif-vider"
                  onClick={() => setPlanif([])}
                  className="h-10 border border-slate-300 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                >
                  Vider
                </button>
              </div>
            </div>
          )}
          <table data-testid="reception-table" className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['', 'Réf.', 'Date', 'Parcours', 'Tiers', 'Qualité', 'Mode', 'Poids net (Kg)', 'Rdt (%)', 'Montant (TND)', 'Statut', 'Paiement', ...(estAdmin ? ['Écart Foss (Kg)'] : []), ''].map((c, i) => (
                  <th key={c || `col-${i}`} className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibles.length === 0 ? (
                <tr data-testid="reception-empty">
                  <td colSpan={13} className="px-3 py-14 text-center text-sm text-slate-500">
                    Aucune réception pour ce filtre.
                  </td>
                </tr>
              ) : (
                visibles.map((r) => {
                  const c = decompte(r);
                  const lamp = r.parcours === 'fournisseur' && familleDe(r.qualite) === 'lampante';
                  const pay = etatPaiement(r);
                  const fond = { impaye: 'bg-red-50/70', solde: 'bg-emerald-50/60', depot: 'bg-sky-50/50' }[pay.code] ?? (lamp ? 'bg-orange-50/40' : '');
                  return (
                    <tr
                      key={r.id}
                      data-testid={`reception-row-${r.id}`}
                      data-paiement={pay.code}
                      className={`border-b border-slate-100 transition-colors duration-150 hover:bg-slate-100/70 ${fond}`}
                    >
                      <td className="px-3 py-2">
                        {r.statut === 'En attente' ? (
                          <button
                            type="button"
                            data-testid={`select-planif-${r.id}`}
                            onClick={() => togglePlanif(r.id)}
                            className="text-slate-700"
                            title="Sélectionner pour l'ordre de fabrication"
                          >
                            {planif.includes(r.id) ? <CheckSquare size={17} /> : <Square size={17} className="text-slate-300" />}
                          </button>
                        ) : (
                          <span className="font-data text-[10px] uppercase text-slate-400">
                            {numeroOf(r.ofId) ? `OF ${numeroOf(r.ofId)}` : ''}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-data text-sm font-semibold text-slate-900">
                        {r.id}
                        {r.parcours === 'client' && <BadgeFacture type="prestation_reception" id={r.id} />}
                        {r.ventile && (
                          <span data-testid={`ventile-${r.id}`} title={`Camion brut ${fmt(r.brutCamion)} / tare ${fmt(r.tareCamion)} kg — ${lignesDuBon(r).length} qualités`} className="block font-data text-[10px] font-normal text-violet-700">
                            ventilé {fmt(r.partPct, 1)} % · camion {fmt(Number(r.brutCamion) - Number(r.tareCamion))} kg
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-data text-sm text-slate-700">{r.date}</td>
                      <td className="px-3 py-2"><ParcoursBadge parcours={r.parcours} /></td>
                      <td className="px-3 py-2 text-sm text-slate-800">{nomTiers(r.tiersId)}</td>
                      <td className="px-3 py-2">
                        {r.qualite ? (
                          <QualiteBadge famille={familleDe(r.qualite)} label={labelQualite(r.qualite)} />
                        ) : (
                          <span className="font-data text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-slate-700">
                        {labelMode(r.mode)}
                        {r.mode === 'negocier' && !r.prixFixeLe && (
                          <span data-testid={`badge-negocier-${r.id}`} className="mt-0.5 block border border-amber-400 bg-amber-50 px-1 py-0.5 font-data text-[10px] font-bold text-amber-800">
                            À négocier — provision au cours figé du {String(r.coursFige?.le ?? '').slice(5, 10).split('-').reverse().join('/')} à {String(r.coursFige?.le ?? '').slice(11, 16)} ({fmt(r.coursFige?.prixApplique ?? 0, 3)} TND/kg)
                          </span>
                        )}
                        {r.modeOrigine === 'negocier' && r.prixFixeLe && (
                          <span data-testid={`badge-fixe-${r.id}`} className="mt-0.5 block border border-emerald-300 bg-emerald-50 px-1 py-0.5 font-data text-[10px] font-bold text-emerald-800">
                            Prix fixé le {String(r.prixFixeLe).slice(0, 10)} · écart {r.ecartFixation >= 0 ? '+' : '−'}{fmt(Math.abs(r.ecartFixation ?? 0), 3)} TND
                          </span>
                        )}
                        {c.tauxDechet > 0 && <span data-testid={`dechet-${r.id}`} className="block font-data text-[10px] text-amber-700">déchet {fmt(c.tauxDechet, 1)} % · utile {fmt(c.poidsUtile)} kg</span>}
                      </td>
                      <td className="tabular px-3 py-2 font-data text-sm text-slate-900">{fmt(c.poidsNet)}</td>
                      <td className="tabular px-3 py-2 font-data text-sm text-slate-900">
                        {c.enAttenteRendement ? '—' : fmt(c.rendement, 2)}
                      </td>
                      <td className="tabular px-3 py-2 font-data text-sm text-slate-900">{fmt(c.montant, 2)}</td>
                      <td className="px-3 py-2">
                        <StatutBadge statut={r.statut} />
                        {(r.emballagesMontant || 0) > 0 && r.emballagesRegle !== true && (
                          <button
                            type="button"
                            data-testid={`alerte-emballage-${r.id}`}
                            onClick={() => setEncaisse(r)}
                            className="mt-1 flex items-center gap-1 border-2 border-red-600 bg-red-50 px-1.5 py-0.5 font-data text-[10px] font-bold uppercase tracking-[0.06em] text-red-700 transition-colors duration-150 hover:bg-red-600 hover:text-white"
                          >
                            <AlertTriangle size={11} /> Emballage à régler — {fmt(r.emballagesMontant, 3)} TND
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col items-start gap-1">
                          <PaiementBadge reception={r} />
                          {pay.code === 'incomplet' && (
                            <button
                              type="button"
                              data-testid={`completer-${r.id}`}
                              onClick={() => setCompleter(r)}
                              className="inline-flex h-8 items-center gap-1.5 border-2 border-slate-900 px-2.5 text-xs font-bold text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                            >
                              <ClipboardList size={13} /> Compléter
                            </button>
                          )}
                          {(pay.code === 'impaye' || pay.code === 'depot') && (
                            <SolderButton reception={r} montant={c.montant} utilisateur={user?.name} onDone={load} />
                          )}
                          {r.ventile && pay.code === 'impaye' && (
                            <SolderCamionButton reception={r} utilisateur={user?.name} onDone={load} />
                          )}
                        </div>
                      </td>
                      {estAdmin && (
                        <td className="tabular px-3 py-2 font-data text-sm font-bold" data-testid={`ecart-${r.id}`}>
                          {ecartRendement(r) === null ? (
                            <span className="text-slate-400">—</span>
                          ) : (
                            <span className={couleurGain(ecartRendement(r))}>
                              {ecartRendement(r) >= 0 ? '+' : '−'}
                              {fmt(Math.abs(ecartRendement(r)), 3)}
                              <span className="ml-1 text-[10px] font-normal uppercase">
                                {ecartRendement(r) >= 0 ? 'bonus' : 'malus'}
                              </span>
                            </span>
                          )}
                        </td>
                      )}
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1.5">
                          <button
                            type="button"
                            data-testid={`modifier-details-${r.id}`}
                            title="Compléter / modifier les détails de la transaction"
                            onClick={() => setCompleter(r)}
                            className="border border-slate-300 p-1.5 text-slate-600 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                          >
                            <ClipboardList size={13} />
                          </button>
                          <button
                            type="button"
                            data-testid={`partage-lien-${r.id}`}
                            title="Envoyer le lien de suivi au client"
                            onClick={() => setPartage(r)}
                            className="border border-slate-300 p-1.5 text-slate-600 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                          >
                            <Send size={13} />
                          </button>
                          <button
                            type="button"
                            data-testid={`etiquettes-${r.id}`}
                            title="Planche d'étiquettes QR"
                            onClick={() => setEtiquettes(r)}
                            className="border border-slate-300 p-1.5 text-slate-600 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                          >
                            <QrCode size={13} />
                          </button>
                          <button
                            type="button"
                            data-testid={`decompte-button-${r.id}`}
                            onClick={() => setDecompteRow(r)}
                            className="inline-flex items-center gap-1.5 border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                          >
                            <Calculator size={13} /> {r.parcours === 'client' ? 'Facture' : 'Décompte'}
                          </button>
                          <ActionsAdmin
                            id={r.id}
                            row={{ ...r, poidsBrut: r.poidsBrut ?? '', tare: r.tare ?? '' }}
                            titre="Corriger la réception"
                            champs={[
                              { key: 'date', label: 'Date', type: 'date' },
                              { key: 'poidsBrut', label: 'Poids brut (Kg)', type: 'number' },
                              { key: 'tare', label: 'Tare (Kg)', type: 'number' },
                              { key: 'qualite', label: 'Qualité', options: QUALITES.map((q) => ({ value: q.id, label: q.label })) },
                              { key: 'statut', label: 'Statut', options: STATUTS },
                              { key: 'prixFixe', label: 'Prix fixe olives (TND/Kg)', type: 'number' },
                              { key: 'prixBaseHuile', label: 'Prix de base huile (TND)', type: 'number' },
                              { key: 'tarifTrituration', label: 'Tarif trituration (TND/Kg)', type: 'number' },
                              { key: 'foss', label: 'Rendement Foss (%)', type: 'number' },
                              {
                                key: 'calamites',
                                label: 'Retenir le fonds de calamité au fournisseur (1 %)',
                                type: 'bool',
                                options: [
                                  { value: 'non', label: 'À charge de l’huilerie' },
                                  { value: 'oui', label: 'Retenu au fournisseur (1 %)' },
                                ],
                              },
                            ]}
                            onModifier={(patch) => adminModifierReception(r.id, patch, user?.name)}
                            onSupprimer={() => adminSupprimerReception(r.id, user?.name)}
                            onDone={load}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      {estAdmin && annulees.length > 0 && (
        <section data-testid="receptions-annulees" className="border border-slate-200 border-l-2 border-l-slate-400 bg-white">
          <div className="border-b border-slate-200 px-4 py-3">
            <h2 className="font-heading text-base font-bold tracking-tight text-slate-900">Bons annulés — piste d'audit ({annulees.length})</h2>
            <p className="text-xs text-slate-500">Aucun bon n'est supprimé physiquement : l'historique complet reste en base.</p>
          </div>
          <table className="w-full border-collapse text-left">
            <tbody className="divide-y divide-slate-100">
              {annulees.map((r) => (
                <tr key={r.id} data-testid={`annulee-${r.id}`} className="text-slate-500 line-through">
                  <td className="px-3 py-1.5 font-data text-xs font-semibold">{r.id}</td>
                  <td className="px-3 py-1.5 text-xs">{r.date}</td>
                  <td className="px-3 py-1.5 text-xs">{nomTiers(r.tiersId)}</td>
                  <td className="px-3 py-1.5 font-data text-xs">{fmt(decompte(r).poidsNet)} kg</td>
                  <td className="px-3 py-1.5 text-xs no-underline">Annulé le {String(r.annuleLe || '').slice(0, 16).replace('T', ' ')} par {r.annulePar || '—'} (était « {r.statutAvant} »)</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {formOpen && (
        <ReceptionForm
          onClose={() => {
            setFormOpen(false);
            if (params.get('nouveau')) setParams({}, { replace: true });
          }}
          onCreated={load}
        />
      )}
      {decompteRow && (
        <DecompteDialog reception={decompteRow} onClose={() => setDecompteRow(null)} onUpdated={load} />
      )}
      {etiquettes && (
        <EtiquettesQR
          reception={{ id: etiquettes.id, date: etiquettes.date, tiers: nomTiers(etiquettes.tiersId) }}
          onClose={() => setEtiquettes(null)}
        />
      )}
      {partage && <PartageLienDialog reception={partage} onClose={() => setPartage(null)} />}
      {avance && <AvanceFournisseurDialog onClose={() => setAvance(false)} onDone={load} />}
      {encaissement && <EncaissementClientDialog onClose={() => setEncaissement(false)} onDone={load} />}
      {fixation && <FixationPrixDialog onClose={() => setFixation(false)} onDone={load} />}
      {eclair && (
        <PeseeEclairForm
          onClose={() => {
            setEclair(false);
            if (params.get('eclair') || params.get('nouveau')) setParams({}, { replace: true });
          }}
          onCreated={(row) => {
            load();
            if (row.statut !== 'Sur site') setCompleter(row);
          }}
        />
      )}
      {completer && <CompleterBonDialog reception={completer} onClose={() => setCompleter(null)} onDone={load} />}
      {encaisse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="encaisser-emballages-modal">
          <div className="w-full max-w-md border-2 border-slate-900 bg-white">
            <div className="border-b border-slate-200 px-5 py-4">
              <p className="font-heading text-lg font-black tracking-tight text-slate-900">
                Encaisser les emballages — {encaisse.id}
              </p>
              <p className="text-sm text-slate-600">{nomTiers(encaisse.tiersId)}</p>
            </div>
            <div className="space-y-3 px-5 py-4">
              <ul className="divide-y divide-slate-100 border border-slate-200">
                {(encaisse.emballagesFournis ?? []).map((e) => (
                  <li key={e.formatId} className="flex justify-between px-3 py-1.5 text-sm">
                    <span className="text-slate-700">{e.nom} × {e.qte}</span>
                    <span className="font-data text-slate-900">{fmt(e.total, 3)} TND</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-between border-2 border-slate-900 bg-slate-900 px-4 py-3 text-white">
                <span className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-300">Total à encaisser</span>
                <span data-testid="montant-emballages" className="tabular font-data text-xl font-semibold">
                  {fmt(encaisse.emballagesMontant, 3)} TND
                </span>
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Compte crédité</p>
              <div className="flex flex-wrap gap-2">
                {comptes.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`encaisser-${c.id}`}
                    onClick={() => validerEncaissement(c.id)}
                    className="flex h-11 items-center gap-2 bg-slate-900 px-3 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700"
                  >
                    <Wallet size={15} /> {c.nom}
                  </button>
                ))}
              </div>
            </div>
            <div className="border-t border-slate-200 px-5 py-3">
              <button
                type="button"
                data-testid="encaisser-annuler"
                onClick={() => setEncaisse(null)}
                className="h-10 border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
